/**
 * Clients, contacts, email domains, SPOC history and client notes — ADR-001 §7.1, §7.6, §11.3.
 *
 * Visibility: managers (founder, ops_ad) see every client; an ops_member sees only clients they are the
 * **current** SPOC of; requesters see none. Out-of-scope ids return `404 not_found`, never `403`.
 *
 * Revenue masking: `annual_revenue_lakh` and `mrr_lakh` are **absent** (not null) for non-managers.
 * Each response that can carry revenue has a `*Member` schema which is `strict` — a leaked revenue key
 * fails the API's own response validation in non-production builds, and the contract test asserts absence.
 */
import { z } from 'zod';
import { IsoDate, Lakh, PageQuery, Timestamp, Uuid, Version, derived, derivedMember, list, page } from './common.js';
import { UserRef } from './users.js';
export const Segment = z.enum(['BFSI', 'QSR', 'Retail']).meta({ id: 'Segment' });
/** Priority is a person's decision, never computed (guide §4). */
export const Priority = z.enum(['high', 'med', 'low']).meta({ id: 'Priority' });
export const ClientStatus = z.enum(['active', 'archived']).meta({ id: 'ClientStatus' });
/** Health band (guide §6.1): healthy ≥ 70, watch 45–69, at_risk < 45. */
export const HealthBand = z.enum(['healthy', 'watch', 'at_risk']).meta({ id: 'HealthBand' });
/** Revenue keys that must be absent for ops_member, sales and marketing (§11.1 field masking). */
export const REVENUE_KEYS = [
    'mrr_lakh',
    'plan_value_lakh',
    'annual_revenue_lakh',
    'value_lakh',
    'revenue_at_risk_lakh',
];
export const ClientIdParams = z.object({ id: Uuid });
// ---------------------------------------------------------------------------
// ClientRef — the compact reference (§11.2)
// ---------------------------------------------------------------------------
export const ClientRef = z
    .object({
    id: Uuid,
    name: z.string(),
    segment: Segment,
    priority: Priority,
    /** The current SPOC. Every client has exactly one (a client with no SPOC is visible to no member). */
    spoc: UserRef,
})
    .meta({ id: 'ClientRef' });
// ---------------------------------------------------------------------------
// GET /clients → Page<ClientRow>
// ---------------------------------------------------------------------------
/** The health score with its reason lines. */
export const ClientHealth = derived(z.number().int().min(0).max(100))
    .extend({ band: HealthBand })
    .meta({ id: 'ClientHealth' });
/** The health score as a caller without `toneReason.read` receives it: no tone reasons (common.ts TONE_REASON_SIGNALS). */
export const ClientHealthMember = derivedMember(z.number().int().min(0).max(100))
    .extend({ band: HealthBand })
    .meta({ id: 'ClientHealthMember' });
const ClientRowBase = ClientRef.extend({
    /** Client since (the All clients card prints "BFSI, since Sep 2024"). Not revenue: no masking. */
    since: IsoDate,
    /** The client's optimistic-concurrency version — send it as If-Match on PATCH /clients/{id} (ADR §11.1). */
    version: Version,
    /**
     * Latest health snapshot (`client_health_daily`). `null` until the worker has stored one for this client (a
     * write that moves the score queues it; the nightly run scores every active client).
     */
    health: ClientHealth.nullable(),
    /** Same as `health.band`, lifted for sorting and filtering. `null` when `health` is `null`. */
    band: HealthBand.nullable(),
    /**
     * Revenue-weighted utilisation in percent, from the latest health snapshot's inputs. Over 100 when the client
     * uses more than they pay for (same range as HealthInputs.weighted_util). `null` with no snapshot or no usage data.
     */
    weighted_util: z.number().min(0).max(400).nullable(),
    /** Revenue-weighted usage trend vs own baseline, in percent (may be negative), from the same snapshot. `null` without one. */
    trend: z.number().nullable(),
    /** Open tasks for this client, any due date (the card prints "{open} open, {overdue} late"). */
    open_task_count: z.number().int().nonnegative(),
    /** Open tasks for this client due before today. */
    overdue_count: z.number().int().nonnegative(),
    open_escalations: z.number().int().nonnegative(),
    /** Days since the last recorded client touchpoint; `null` when none is recorded. */
    last_contact_days: z.number().int().nonnegative().nullable(),
});
/** ClientRow as an ops_member sees it — strict: revenue keys may not appear, nor tone reasons in `health`. */
export const ClientRowMember = ClientRowBase.extend({ health: ClientHealthMember.nullable() }).strict().meta({ id: 'ClientRowMember' });
/** ClientRow as a manager sees it. */
export const ClientRowManager = ClientRowBase.extend({ annual_revenue_lakh: Lakh.nullable() })
    .strict()
    .meta({ id: 'ClientRowManager' });
export const ClientRow = z.union([ClientRowManager, ClientRowMember]).meta({ id: 'ClientRow' });
export const ClientRowPage = page(ClientRow).meta({ id: 'ClientRowPage' });
export const ClientSort = z.enum(['health', 'name', 'priority', 'overdue', 'last_contact']);
export const ListClientsQuery = PageQuery.extend({
    segment: Segment.optional(),
    priority: Priority.optional(),
    band: HealthBand.optional(),
    /** Managers only; for a member it is ignored (they only ever see their own). */
    spoc_id: Uuid.optional(),
    /** Matches client name, case-insensitive. */
    q: z.string().trim().min(1).max(80).optional(),
    /**
     * `health` (default) = worst first, clients without a score last; `name` A–Z; `priority` high→low;
     * `overdue` most first; `last_contact` longest silence first.
     */
    sort: ClientSort.default('health'),
    status: ClientStatus.default('active'),
});
// ---------------------------------------------------------------------------
// Contacts and email domains
// ---------------------------------------------------------------------------
const Phone = z
    .string()
    .trim()
    .regex(/^\+?[0-9]{10,15}$/, 'Enter the phone number with country code, digits only, for example +919820012345');
export const ClientContact = z
    .object({
    id: Uuid,
    client_id: Uuid,
    name: z.string(),
    title: z.string(),
    email: z.email().nullable(),
    phone: z.string().nullable(),
    is_primary: z.boolean(),
    version: Version,
})
    .meta({ id: 'ClientContact' });
export const ClientContactList = list(ClientContact).meta({ id: 'ClientContactList' });
export const CreateContactBody = z
    .object({
    name: z.string().trim().min(1, 'Enter the contact’s name').max(80),
    title: z.string().trim().min(1, 'Enter their job title, for example Head of Marketing').max(80),
    email: z.email('Enter a full email address').optional(),
    phone: Phone.optional(),
    /** Setting a primary contact clears the flag on the client’s previous primary. */
    is_primary: z.boolean().optional(),
})
    .strict();
export const UpdateContactBody = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    title: z.string().trim().min(1).max(80).optional(),
    email: z.email().nullable().optional(),
    phone: Phone.nullable().optional(),
    is_primary: z.boolean().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
export const ContactParams = z.object({ id: Uuid, contact_id: Uuid });
/** Email domains recognise a mail thread as this client's (§10.3). Unique across all clients. */
export const ClientEmailDomain = z
    .object({
    id: Uuid,
    client_id: Uuid,
    domain: z.string(),
    created_at: Timestamp,
})
    .meta({ id: 'ClientEmailDomain' });
export const ClientEmailDomainList = list(ClientEmailDomain).meta({ id: 'ClientEmailDomainList' });
export const CreateEmailDomainBody = z
    .object({
    domain: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'Enter just the domain, for example hdfcbank.com — no @ and no https://'),
})
    .strict();
export const EmailDomainParams = z.object({ id: Uuid, domain_id: Uuid });
// ---------------------------------------------------------------------------
// Client products (step-1 shape; usage-derived fields arrive in step 5)
// ---------------------------------------------------------------------------
export const ClientProductStatus = z.enum(['active', 'paused', 'ended']).meta({ id: 'ClientProductStatus' });
const ClientProductBase = z.object({
    id: Uuid,
    product_key: z.string(),
    product_name: z.string(),
    /** What the client pays for per period, in the product's unit (not money). */
    plan: z.number().nonnegative().nullable(),
    /** The client's own usual level, in the product's unit. */
    base: z.number().nonnegative().nullable(),
    status: ClientProductStatus,
    started_on: IsoDate.nullable(),
    /** Where this client's numbers come from, when it overrides the product default; `null` = the product's source. */
    source: z.enum(['api', 'csv', 'manual']).nullable(),
});
export const ClientProductMember = ClientProductBase.strict().meta({ id: 'ClientProductMember' });
export const ClientProductManager = ClientProductBase.extend({ mrr_lakh: Lakh.nullable() })
    .strict()
    .meta({ id: 'ClientProductManager' });
// ---------------------------------------------------------------------------
// Client — GET /clients/{id}, POST /clients, PATCH /clients/{id}
// ---------------------------------------------------------------------------
export const PrioritySuggestion = z
    .object({
    /** What the view suggests. Only a person ever writes `priority`. */
    suggested: Priority,
    /** Plain-English reasons, e.g. "High-value account", "2 cross-sells in progress", "Strategic account". */
    reasons: z.array(z.string()),
})
    .meta({ id: 'PrioritySuggestion' });
const ClientBase = ClientRef.extend({
    since: IsoDate,
    /** Monthly effort budget in hours. */
    monthly_hours: z.number().nonnegative(),
    strategic: z.boolean(),
    priority_note: z.string().nullable(),
    status: ClientStatus,
    /** Derived: `since` is in 2026 — shortens the training cadence (guide §6.7). */
    is_new_client: z.boolean(),
    is_seed: z.boolean(),
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
});
/** Response of POST /clients and PATCH /clients/{id}, and the `client` in SPOC change. */
export const ClientMember = ClientBase.strict().meta({ id: 'ClientMember' });
export const ClientManager = ClientBase.extend({ annual_revenue_lakh: Lakh.nullable() })
    .strict()
    .meta({ id: 'ClientManager' });
export const Client = z.union([ClientManager, ClientMember]).meta({ id: 'Client' });
const DetailExtras = {
    contacts: z.array(ClientContact),
    priority_suggestion: PrioritySuggestion,
};
/** GET /clients/{id} — the client with contacts, products and the priority suggestion. */
export const ClientDetailMember = ClientBase.extend({
    ...DetailExtras,
    products: z.array(ClientProductMember),
})
    .strict()
    .meta({ id: 'ClientDetailMember' });
export const ClientDetailManager = ClientBase.extend({
    annual_revenue_lakh: Lakh.nullable(),
    ...DetailExtras,
    products: z.array(ClientProductManager),
})
    .strict()
    .meta({ id: 'ClientDetailManager' });
export const ClientDetail = z.union([ClientDetailManager, ClientDetailMember]).meta({ id: 'ClientDetail' });
const MonthlyHours = z
    .number()
    .min(0, 'Monthly hours cannot be negative')
    .max(744, 'Monthly hours cannot be more than the hours in a month');
/** POST /clients — managers only. `spoc_id` is required: a client with no SPOC is invisible to members. */
export const CreateClientBody = z
    .object({
    name: z.string().trim().min(1, 'Enter the client’s name').max(120),
    segment: Segment,
    since: IsoDate,
    monthly_hours: MonthlyHours,
    priority: Priority,
    annual_revenue_lakh: Lakh.optional(),
    spoc_id: Uuid,
    strategic: z.boolean().optional(),
    priority_note: z.string().trim().max(500).optional(),
})
    .strict();
/**
 * PATCH /clients/{id}. Managers may change every field. An ops_member may change only their own
 * client's `name`, `segment` and `monthly_hours`: sending `annual_revenue_lakh`, `priority`,
 * `strategic` or `priority_note` is `403 forbidden` with `field_errors` naming the field.
 * A `priority` change also writes a `client_notes` row (kind `priority_change`).
 */
export const UpdateClientBody = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    segment: Segment.optional(),
    priority: Priority.optional(),
    monthly_hours: MonthlyHours.optional(),
    annual_revenue_lakh: Lakh.nullable().optional(),
    strategic: z.boolean().optional(),
    priority_note: z.string().trim().max(500).nullable().optional(),
    status: ClientStatus.optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
/** Fields only a manager may write on PATCH /clients/{id}. */
export const CLIENT_MANAGER_ONLY_FIELDS = [
    'annual_revenue_lakh',
    'priority',
    'strategic',
    'priority_note',
    'status',
];
// ---------------------------------------------------------------------------
// SPOC — history and change (the Team → SPOCs tab, "change with a reason")
// ---------------------------------------------------------------------------
export const SpocAssignment = z
    .object({
    id: Uuid,
    user: UserRef,
    from_date: IsoDate,
    /** `null` = the current SPOC (exactly one open row per client). */
    to_date: IsoDate.nullable(),
    reason: z.string().nullable(),
    /** Who made the change; `null` for the first assignment made by a seed or migration. */
    changed_by: UserRef.nullable(),
    is_current: z.boolean(),
})
    .meta({ id: 'SpocAssignment' });
/** Full dated history, newest first. */
export const SpocHistory = list(SpocAssignment).meta({ id: 'SpocHistory' });
/**
 * POST /clients/{id}/spoc — capability `client.changeSpoc` (the founder and the associate director).
 * Closes the open row (to_date = today), opens a new one, optionally moves open tasks to the new SPOC
 * (one assignment-log entry each) and writes a client note (kind `spoc_change`). All in one transaction.
 * The new SPOC must be an active ops_ad or ops_member (`422`), and not the current SPOC (`409`).
 */
export const ChangeSpocBody = z
    .object({
    user_id: Uuid,
    reason: z
        .string()
        .trim()
        .min(3, 'Say why the SPOC is changing, for example "Rebalancing workload"')
        .max(500, 'Keep the reason under 500 characters'),
    move_open_tasks: z.boolean(),
})
    .strict();
export const ChangeSpocResponse = z
    .object({
    client: Client,
    moved_task_count: z.number().int().nonnegative(),
})
    .meta({ id: 'ChangeSpocResponse' });
// ---------------------------------------------------------------------------
// Client notes
// ---------------------------------------------------------------------------
export const ClientNoteKind = z
    .enum(['voice_statement', 'priority_change', 'spoc_change', 'manual'])
    .meta({ id: 'ClientNoteKind' });
export const ClientNote = z
    .object({
    id: Uuid,
    client_id: Uuid,
    at: Timestamp,
    /** `null` when written by a job. */
    by: UserRef.nullable(),
    text: z.string(),
    kind: ClientNoteKind,
})
    .meta({ id: 'ClientNote' });
export const ClientNotePage = page(ClientNote).meta({ id: 'ClientNotePage' });
export const ListClientNotesQuery = PageQuery.extend({ kind: ClientNoteKind.optional() });
//# sourceMappingURL=clients.js.map